import {createSlice, PayloadAction} from '@reduxjs/toolkit';

export type DataSource = 'xrf' | 'minerals';

export interface GradientStop {
    position: number; // 0..1, normalized within the [0, maxAmount] value range
    color: string;    // '#rrggbb'
}

interface ChartStateDate {
    selectLine: any,
    selectedItem: any,
    selectedBoreholes: any[],
    selectedDrillholeId: string | undefined,
    minMax: any,
    colorTable: any[],
    colorTableXrf: any[],
    colorTableMinerals: any[],
    colorGradientXrf: GradientStop[],
    colorGradientMinerals: GradientStop[],
    colorByElementXrf: boolean,
    colorByElementMinerals: boolean,
    elements: any[],
    activeElement: any,
    drillholeDetails: Record<string, { info?: any, minerals?: any, xrf?: any }>,
    offset: number,
    dataSources: DataSource[],
    coreViewerDataSources: DataSource[],
    boreholes3DDataSources: DataSource[],
    chartActiveOverrides: Record<string, boolean>,
    coreViewerActiveOverrides: Record<string, boolean>,
    boreholes3DActiveOverrides: Record<string, boolean>,
    xrfElements: any[],
    mineralsElements: any[],
    isControlBarCollapsed: boolean,
}

const storedLine = localStorage.getItem('line');
const parsedLine = (() => {
    try {
        if (storedLine && storedLine !== 'undefined') {
            const arr = JSON.parse(storedLine);
            if (Array.isArray(arr) && arr.length === 2) {
                return {x1: arr[0][0], y1: arr[0][1], x2: arr[1][0], y2: arr[1][1]};
            }
        }
    } catch (_) {}
    return undefined;
})();

const tagSource = (els: any[], source: DataSource): any[] =>
    els.map((el: any) => (el?.source === source ? el : {...el, source}));

const loadStoredElements = (key: string, source: DataSource): any[] => {
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr) && arr.length > 0) return tagSource(arr, source);
        }
    } catch (_) {}
    return [];
};

const storedXrfElements = loadStoredElements('xrfElements', 'xrf');
const storedMineralsElements = loadStoredElements('mineralsElements', 'minerals');

const readStoredDataSources = (key: string, fallback: DataSource[]): DataSource[] => {
    const rawArr = localStorage.getItem(key);
    if (rawArr) {
        try {
            const arr = JSON.parse(rawArr);
            if (Array.isArray(arr)) {
                const valid = arr.filter((v: any) => v === 'xrf' || v === 'minerals') as DataSource[];
                if (valid.length > 0) return Array.from(new Set(valid));
            }
        } catch (_) {}
    }
    return fallback;
};

const storedDataSources: DataSource[] = (() => {
    const fromMain = readStoredDataSources('dataSources', []);
    if (fromMain.length > 0) return fromMain;
    // Legacy: single dataSource value
    const raw = localStorage.getItem('dataSource');
    if (raw === 'minerals' || raw === 'xrf') return [raw as DataSource];
    return ['xrf'];
})();
const storedCoreViewerDataSources: DataSource[] = readStoredDataSources('coreViewerDataSources', storedDataSources);
const storedBoreholes3DDataSources: DataSource[] = readStoredDataSources('boreholes3DDataSources', storedDataSources);

export const DEFAULT_GRADIENT: GradientStop[] = [
    {position: 0, color: '#f54242'},
    {position: 0.2, color: '#f5ad42'},
    {position: 0.4, color: '#eff542'},
    {position: 0.6, color: '#8df542'},
    {position: 0.8, color: '#42f5bc'},
    {position: 1, color: '#42daf5'},
];

const readStoredGradient = (key: string): GradientStop[] => {
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr) && arr.length >= 2) {
                const valid = arr
                    .filter((s: any) => typeof s?.position === 'number' && typeof s?.color === 'string')
                    .map((s: any) => ({position: Math.max(0, Math.min(1, s.position)), color: s.color}))
                    .sort((a, b) => a.position - b.position);
                if (valid.length >= 2) return valid;
            }
        }
    } catch (_) {}
    return DEFAULT_GRADIENT.map(s => ({...s}));
};

const readStoredActiveOverrides = (key: string): Record<string, boolean> => {
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            const obj = JSON.parse(raw);
            if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
                const out: Record<string, boolean> = {};
                for (const k of Object.keys(obj)) {
                    if (typeof obj[k] === 'boolean') out[k] = obj[k];
                }
                return out;
            }
        }
    } catch (_) {}
    return {};
};

const combineElements = (
    sources: DataSource[],
    xrf: any[],
    minerals: any[],
): any[] => {
    const out: any[] = [];
    if (sources.includes('xrf')) out.push(...xrf);
    if (sources.includes('minerals')) out.push(...minerals);
    return out;
};

const initialState: ChartStateDate = {
    selectLine: parsedLine,
    selectedItem: undefined,
    selectedBoreholes: [],
    selectedDrillholeId: localStorage.getItem('selectedDrillholeId') || undefined,
    minMax: { factor: parseFloat(localStorage.getItem('heightFactor') || '1') },
    colorTable: [],
    colorTableXrf: [],
    colorTableMinerals: [],
    colorGradientXrf: readStoredGradient('colorGradientXrf'),
    colorGradientMinerals: readStoredGradient('colorGradientMinerals'),
    colorByElementXrf: localStorage.getItem('colorByElementXrf') === 'true',
    colorByElementMinerals: localStorage.getItem('colorByElementMinerals') === 'true',
    elements: combineElements(storedDataSources, storedXrfElements, storedMineralsElements),
    activeElement: undefined,
    drillholeDetails: {},
    offset: parseInt(localStorage.getItem('offset') || '10'),
    dataSources: storedDataSources,
    coreViewerDataSources: storedCoreViewerDataSources,
    boreholes3DDataSources: storedBoreholes3DDataSources,
    chartActiveOverrides: readStoredActiveOverrides('chartActiveOverrides'),
    coreViewerActiveOverrides: readStoredActiveOverrides('coreViewerActiveOverrides'),
    boreholes3DActiveOverrides: readStoredActiveOverrides('boreholes3DActiveOverrides'),
    xrfElements: storedXrfElements,
    mineralsElements: storedMineralsElements,
    isControlBarCollapsed: localStorage.getItem('isControlBarCollapsed') === 'true',
};

const persistSources = (state: ChartStateDate) => {
    localStorage.setItem('dataSources', JSON.stringify(state.dataSources));
    // Keep legacy single key in sync for tools that still read it
    localStorage.setItem('dataSource', state.dataSources[0] ?? 'xrf');
};

const recomputeElements = (state: ChartStateDate) => {
    state.elements = combineElements(state.dataSources, state.xrfElements, state.mineralsElements);
    localStorage.setItem('elements', JSON.stringify(state.elements));
};

export const chartStateSlice: any = createSlice({
    name: 'elementChanges',
    initialState,
    reducers: {
        setSelectLine: (state, action: PayloadAction<any>): any => {
            state.selectLine = action.payload;
            if (action.payload && action.payload.x1 != null) {
                localStorage.setItem('line', JSON.stringify([
                    [action.payload.x1, action.payload.y1],
                    [action.payload.x2, action.payload.y2],
                ]));
            } else {
                localStorage.removeItem('line');
            }
        },
        setSelectedItem: (state, action: PayloadAction<any>): any => {
            state.selectedItem = action.payload;
        },
        setSelectedBoreholes: (state, action: PayloadAction<any[]>): any => {
            state.selectedBoreholes = action.payload;
        },
        setMinMax: (state, action: PayloadAction<any>): any => {
            state.minMax = action.payload;
            if (action.payload && action.payload.factor != null) {
                localStorage.setItem('heightFactor', String(action.payload.factor));
            }
        },
        setColorTable: (state, action: PayloadAction<any[]>): any => {
            state.colorTable = action.payload;
        },
        setColorTableXrf: (state, action: PayloadAction<any[]>): any => {
            state.colorTableXrf = action.payload;
        },
        setColorTableMinerals: (state, action: PayloadAction<any[]>): any => {
            state.colorTableMinerals = action.payload;
        },
        setColorGradientXrf: (state, action: PayloadAction<GradientStop[]>): any => {
            const sorted = [...(action.payload ?? [])].sort((a, b) => a.position - b.position);
            state.colorGradientXrf = sorted;
            localStorage.setItem('colorGradientXrf', JSON.stringify(sorted));
        },
        setColorGradientMinerals: (state, action: PayloadAction<GradientStop[]>): any => {
            const sorted = [...(action.payload ?? [])].sort((a, b) => a.position - b.position);
            state.colorGradientMinerals = sorted;
            localStorage.setItem('colorGradientMinerals', JSON.stringify(sorted));
        },
        setColorByElementXrf: (state, action: PayloadAction<boolean>): any => {
            state.colorByElementXrf = !!action.payload;
            localStorage.setItem('colorByElementXrf', String(!!action.payload));
        },
        setColorByElementMinerals: (state, action: PayloadAction<boolean>): any => {
            state.colorByElementMinerals = !!action.payload;
            localStorage.setItem('colorByElementMinerals', String(!!action.payload));
        },
        setElements: (state, action: PayloadAction<any[]>): any => {
            // Accepts combined element list (possibly tagged with source). Split back
            // into per-source arrays so single-source legacy callers stay consistent.
            const payload = action.payload ?? [];
            const xrf: any[] = [];
            const minerals: any[] = [];
            for (const el of payload) {
                if (el?.source === 'minerals') minerals.push(el);
                else if (el?.source === 'xrf') xrf.push(el);
                else {
                    // Untagged element: assume it belongs to the currently dominant source
                    if (state.dataSources.includes('xrf')) xrf.push({...el, source: 'xrf'});
                    else if (state.dataSources.includes('minerals')) minerals.push({...el, source: 'minerals'});
                }
            }
            if (xrf.length > 0 || state.dataSources.includes('xrf')) {
                state.xrfElements = xrf.length > 0 ? xrf : state.xrfElements;
            }
            if (minerals.length > 0 || state.dataSources.includes('minerals')) {
                state.mineralsElements = minerals.length > 0 ? minerals : state.mineralsElements;
            }
            localStorage.setItem('xrfElements', JSON.stringify(state.xrfElements));
            localStorage.setItem('mineralsElements', JSON.stringify(state.mineralsElements));
            recomputeElements(state);
        },
        setXrfElements: (state, action: PayloadAction<any[]>): any => {
            state.xrfElements = tagSource(action.payload ?? [], 'xrf');
            localStorage.setItem('xrfElements', JSON.stringify(state.xrfElements));
            recomputeElements(state);
        },
        setMineralsElements: (state, action: PayloadAction<any[]>): any => {
            state.mineralsElements = tagSource(action.payload ?? [], 'minerals');
            localStorage.setItem('mineralsElements', JSON.stringify(state.mineralsElements));
            recomputeElements(state);
        },
        setDataSources: (state, action: PayloadAction<DataSource[]>): any => {
            const next = Array.from(new Set(action.payload ?? [])) as DataSource[];
            state.dataSources = next;
            state.activeElement = undefined;
            persistSources(state);
            recomputeElements(state);
        },
        setDataSource: (state, action: PayloadAction<DataSource>): any => {
            // Legacy single-value setter: collapses to a one-element array.
            state.dataSources = [action.payload];
            state.activeElement = undefined;
            persistSources(state);
            recomputeElements(state);
        },
        setCoreViewerDataSources: (state, action: PayloadAction<DataSource[]>): any => {
            const next = Array.from(new Set(action.payload ?? [])) as DataSource[];
            state.coreViewerDataSources = next;
            localStorage.setItem('coreViewerDataSources', JSON.stringify(next));
        },
        setBoreholes3DDataSources: (state, action: PayloadAction<DataSource[]>): any => {
            const next = Array.from(new Set(action.payload ?? [])) as DataSource[];
            state.boreholes3DDataSources = next;
            localStorage.setItem('boreholes3DDataSources', JSON.stringify(next));
        },
        setChartActiveOverrides: (state, action: PayloadAction<Record<string, boolean>>): any => {
            const next = action.payload ?? {};
            state.chartActiveOverrides = next;
            localStorage.setItem('chartActiveOverrides', JSON.stringify(next));
        },
        setCoreViewerActiveOverrides: (state, action: PayloadAction<Record<string, boolean>>): any => {
            const next = action.payload ?? {};
            state.coreViewerActiveOverrides = next;
            localStorage.setItem('coreViewerActiveOverrides', JSON.stringify(next));
        },
        setBoreholes3DActiveOverrides: (state, action: PayloadAction<Record<string, boolean>>): any => {
            const next = action.payload ?? {};
            state.boreholes3DActiveOverrides = next;
            localStorage.setItem('boreholes3DActiveOverrides', JSON.stringify(next));
        },
        setActiveElement: (state, action: PayloadAction<any>): any => {
            state.activeElement = action.payload;
        },
        setDrillholeDetail: (state, action: PayloadAction<{ holeId: string, data: { info?: any, minerals?: any, xrf?: any } }>): any => {
            state.drillholeDetails[action.payload.holeId] = {
                ...state.drillholeDetails[action.payload.holeId],
                ...action.payload.data,
            };
        },
        clearDrillholeDetails: (state): any => {
            state.drillholeDetails = {};
        },
        setSelectedDrillholeId: (state, action: PayloadAction<string | undefined>): any => {
            state.selectedDrillholeId = action.payload;
            if (action.payload) {
                localStorage.setItem('selectedDrillholeId', action.payload);
            } else {
                localStorage.removeItem('selectedDrillholeId');
            }
        },
        setOffset: (state, action: PayloadAction<number>): any => {
            state.offset = action.payload;
            localStorage.setItem('offset', String(action.payload));
        },
        setControlBarCollapsed: (state, action: PayloadAction<boolean>): any => {
            state.isControlBarCollapsed = action.payload;
            localStorage.setItem('isControlBarCollapsed', String(action.payload));
        },
    },
});

export const {
    setSelectLine,
    setSelectedItem,
    setSelectedBoreholes,
    setMinMax,
    setColorTable,
    setColorTableXrf,
    setColorTableMinerals,
    setColorGradientXrf,
    setColorGradientMinerals,
    setColorByElementXrf,
    setColorByElementMinerals,
    setElements,
    setXrfElements,
    setMineralsElements,
    setDataSources,
    setDataSource,
    setCoreViewerDataSources,
    setBoreholes3DDataSources,
    setChartActiveOverrides,
    setCoreViewerActiveOverrides,
    setBoreholes3DActiveOverrides,
    setActiveElement,
    setDrillholeDetail,
    clearDrillholeDetails,
    setSelectedDrillholeId,
    setOffset,
    setControlBarCollapsed,
} = chartStateSlice.actions;

export default chartStateSlice.reducer;
